import * as Sentry from "@sentry/react";
import jsQR from "jsqr";
import { Flashlight, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { extractRefCandidates, matchRef, type RefMatch } from "./matchRef";
import { type RefOcr } from "./ocr";

/** Minimal typing for the native detector (Chrome/Android); absent elsewhere. */
interface NativeBarcodeDetector {
  detect(source: ImageBitmapSource): Promise<{ rawValue: string }[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: new (opts: {
      formats: string[];
    }) => NativeBarcodeDetector;
  }
}

/**
 * Full-screen camera. Every frame is tried for a QR (native detector
 * where the phone has one, jsQR otherwise); the guide-box crop goes to
 * the OCR worker at most twice a second. Anything read is handed up as
 * a match against the event's known refs. The overlay never decides
 * who a guest is, it only reads.
 */
export function ScanOverlay({
  knownRefs,
  ocr,
  ocrError = null,
  onRead,
  onClose,
}: {
  knownRefs: readonly string[];
  /** Null until the worker has loaded; QR keeps working meanwhile. */
  ocr: RefOcr | null;
  /** Set when the reader failed to load, so nobody waits on it. */
  ocrError?: string | null;
  /** A match (or a well-formed ref that isn't on this list) was read;
   *  `person` is the 1-based party position when the code was one
   *  person's own pass. */
  onRead: (match: RefMatch, raw: string, person: number | null) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLCanvasElement>(null);
  const cropRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [torch, setTorch] = useState<boolean | null>(null);
  const [status, setStatus] = useState<"starting" | "live">("starting");
  const [quiet, setQuiet] = useState(false);
  // What the reader last saw is still recorded by the camera loop (left
  // exactly as it was for event week) but no longer shown: raw OCR text
  // looked like an error to volunteers.
  const [, setLastText] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastRead = useRef<{ raw: string; at: number } | null>(null);
  const lastHitAt = useRef<number>(Date.now());
  // The guest list refreshes every few seconds and the OCR worker lands
  // late; neither should restart the camera. Read them through refs.
  const knownRef = useRef(knownRefs);
  knownRef.current = knownRefs;
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;
  const ocrRef = useRef(ocr);
  ocrRef.current = ocr;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    let ocrBusy = false;
    let lastOcrAt = 0;
    let lastQrAt = 0;
    const detector = window.BarcodeDetector
      ? new window.BarcodeDetector({ formats: ["qr_code"] })
      : null;

    const handleText = (raw: string) => {
      const now = Date.now();
      const last = lastRead.current;
      if (last && last.raw === raw && now - last.at < 5000) {
        return;
      }
      const candidates = extractRefCandidates(raw);
      for (const c of candidates) {
        const m = matchRef(c.ref, knownRef.current);
        lastRead.current = { raw, at: now };
        lastHitAt.current = now;
        setQuiet(false);
        onReadRef.current(m, c.ref, c.person);
        return;
      }
    };

    const tick = async () => {
      if (stopped) {
        return;
      }
      const video = videoRef.current;
      const frame = frameRef.current;
      const crop = cropRef.current;
      if (video && frame && crop && video.readyState >= 2) {
        const now = Date.now();
        // ── QR: ~10/s ──
        if (now - lastQrAt > 100) {
          lastQrAt = now;
          const w = 640;
          const h =
            Math.round((video.videoHeight / video.videoWidth) * w) || 480;
          frame.width = w;
          frame.height = h;
          const ctx = frame.getContext("2d", { willReadFrequently: true });
          if (ctx) {
            ctx.drawImage(video, 0, 0, w, h);
            try {
              if (detector) {
                const codes = await detector.detect(frame);
                const first = codes[0];
                if (first?.rawValue) {
                  handleText(first.rawValue);
                }
              } else {
                const img = ctx.getImageData(0, 0, w, h);
                const code = jsQR(img.data, w, h, {
                  inversionAttempts: "dontInvert",
                });
                if (code?.data) {
                  handleText(code.data);
                }
              }
            } catch {
              // a bad frame is just a bad frame
            }
            // ── OCR on the guide box: ≤2/s, cut from the FULL-resolution
            // video; a downscaled frame leaves a phone's text too small.
            const ocrNow = ocrRef.current;
            if (ocrNow && !ocrBusy && now - lastOcrAt > 500) {
              ocrBusy = true;
              lastOcrAt = now;
              const vw = video.videoWidth || w;
              const vh = video.videoHeight || h;
              const bw = Math.round(vw * 0.86);
              const bh = Math.round(vh * 0.28);
              const bx = Math.round((vw - bw) / 2);
              const by = Math.round((vh - bh) / 2);
              // Upscale small captures so glyphs land near 30-40 px tall.
              const scale = bw < 1200 ? 2 : 1;
              crop.width = bw * scale;
              crop.height = bh * scale;
              const cctx = crop.getContext("2d");
              if (cctx) {
                cctx.drawImage(
                  video,
                  bx,
                  by,
                  bw,
                  bh,
                  0,
                  0,
                  bw * scale,
                  bh * scale,
                );
                void ocrNow
                  .recognise(crop)
                  .then((text) => {
                    if (stopped) {
                      return;
                    }
                    const clean = text.replace(/\s+/g, " ").trim();
                    setLastText(clean ? clean.slice(0, 40) : "(nothing)");
                    if (clean) {
                      handleText(text);
                    }
                  })
                  .catch((err: Error) => {
                    if (stopped) {
                      return;
                    }
                    setReadError(err.message || "read failed");
                    Sentry.captureException(err, {
                      tags: { area: "reception-ocr-read" },
                    });
                  })
                  .finally(() => {
                    ocrBusy = false;
                  });
              } else {
                ocrBusy = false;
              }
            }
          }
        }
        if (Date.now() - lastHitAt.current > 3000) {
          setQuiet(true);
        }
      }
      raf = requestAnimationFrame(() => {
        void tick();
      });
    };

    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        audio: false,
      })
      .then((s) => {
        if (stopped) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        streamRef.current = s;
        const video = videoRef.current;
        if (!video) {
          return;
        }
        video.srcObject = s;
        void video.play().then(() => setStatus("live"));
        const track = s.getVideoTracks()[0];
        // `torch` is a non-standard capability the TS lib omits; read it
        // through an `in` check so no shape is assumed.
        const caps = track?.getCapabilities?.();
        const torchSupported =
          caps !== undefined && "torch" in caps && caps.torch === true;
        setTorch(torchSupported ? false : null);
        raf = requestAnimationFrame(() => {
          void tick();
        });
      })
      .catch((err: Error) => {
        const name = err.name;
        setError(
          name === "NotAllowedError"
            ? "The camera is blocked for this site. Allow camera access in your browser settings, or tap Close camera and search by name."
            : "This device's camera isn't available. Tap Close camera and search by name instead.",
        );
      });

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Escape closes the camera. Its own effect, so the camera loop above
  // never restarts because of it. An open guest card takes Escape first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleTorch = () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || torch === null) {
      return;
    }
    const next = !torch;
    // SAFETY: `torch` is a non-standard constraint the TS lib omits; the
    // track reported torch support in getCapabilities() above, which is
    // the only way `torch` is non-null here.
    const torchConstraint = { torch: next } as MediaTrackConstraintSet;
    void track
      .applyConstraints({ advanced: [torchConstraint] })
      .then(() => setTorch(next))
      .catch(() => undefined);
  };

  const readerFailed = Boolean(ocrError || readError);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Scan a pass"
      className="fixed inset-0 z-50 flex flex-col bg-black text-white"
    >
      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          playsInline
          muted
        />
        <canvas ref={frameRef} className="hidden" />
        <canvas ref={cropRef} className="hidden" />
        {/* guide box: its size and position are the OCR crop above */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-[28%] w-[86%] rounded-xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
        </div>
        {error ? null : (
          <>
            {/* Anchored by its bottom edge just above the box, so a
                two-line instruction grows upwards and never covers it. */}
            <p className="pointer-events-none absolute inset-x-6 bottom-[calc(64%+0.75rem)] mx-auto max-w-md rounded-xl bg-black/70 px-4 py-2.5 text-center text-[16px] leading-snug font-medium text-white">
              Hold the QR code or reference number inside the box
            </p>
            <div
              aria-live="polite"
              className="pointer-events-none absolute inset-x-6 top-[calc(50%+16%)] mx-auto max-w-md rounded-xl bg-black/70 px-4 py-2.5 text-center text-[15px] text-white"
            >
              {status === "starting"
                ? "Starting the camera…"
                : quiet
                  ? "Hold still and move a little closer"
                  : "Looking for a pass…"}
              {status === "live" && readerFailed ? (
                <span className="mt-1 block text-[14px] text-white">
                  Printed reference numbers can&apos;t be read on this phone
                  right now. QR codes still work.
                </span>
              ) : null}
            </div>
          </>
        )}
        {error ? (
          <div
            role="alert"
            className="absolute inset-x-6 top-1/2 -translate-y-1/2 rounded-xl bg-black/80 p-5 text-center text-[16px] leading-relaxed"
          >
            {error}
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex min-h-14 items-center gap-2 rounded-full border border-white/50 px-5 text-[16px] font-medium"
        >
          <X size={20} aria-hidden="true" /> Close camera
        </button>
        {torch !== null ? (
          <button
            type="button"
            onClick={toggleTorch}
            aria-pressed={torch}
            className={`inline-flex min-h-14 items-center gap-2 rounded-full border border-white/50 px-5 text-[16px] font-medium ${
              torch ? "bg-white text-black" : ""
            }`}
          >
            <Flashlight size={20} aria-hidden="true" />
            Torch
          </button>
        ) : null}
      </div>
    </div>
  );
}
